import type { MetaWorkflowDocument } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { transform, transformMultipleWorkflows } from './GetReadViewWorkflowTransformer.tsx';
import type {
	ApiReadViewWorkflowResponse,
	ReadViewWorkflowIdentifier,
} from './ReadViewWorkflowIdentifier.types.tsx';
import { validateLogicalDocumentIntegrity } from './WorkflowIntegrityError.tsx';

const getApiPrefix = () => 'gateway/api/jira/project-configuration';

type GetReadViewWorkflowPayload = {
	workflowIds?: string[];
	workflowNames?: [string];
	issueTypeIds?: [string];
	projectId: string;
};

const getPayload = (workflowIdentifier: ReadViewWorkflowIdentifier): GetReadViewWorkflowPayload => {
	const { workflowId, workflowIds, workflowName, issueTypeId, projectId } = workflowIdentifier;

	const payload: GetReadViewWorkflowPayload = { projectId };

	if (workflowId != null) {
		payload.workflowIds = [workflowId];
	}

	if (workflowIds != null) {
		payload.workflowIds = workflowIds;
	}

	if (workflowName != null) {
		payload.workflowNames = [workflowName];
	}

	if (issueTypeId != null) {
		payload.issueTypeIds = [issueTypeId];
	}

	return payload;
};

export type RequestData = ReadViewWorkflowIdentifier & { cloudId: string };

/* deprecated - usages should be migrated to getReadViewWorkflowNew (and getReadViewWorkflowNew renmed) */
export const getReadViewWorkflow = async (requestData: RequestData) => {
	const { cloudId, ...workflowIdentifier } = requestData;
	const url = `/${getApiPrefix()}/query/${cloudId}/2/workflow/read-view`;
	const payload = getPayload(workflowIdentifier);
	const result = await performPostRequest(url, {
		body: JSON.stringify(payload),
	});

	try {
		validateLogicalDocumentIntegrity(result);
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		// an error here will not block the user experience, but it triggers
		// a priority alarm for us to investigate and understand the occurrence.
		log.safeErrorWithoutCustomerData('workflow.render.integrity.checker', message);
	}

	return transform(result);
};

const fetchReadViewWorkflow = async (
	requestData: ReadViewWorkflowIdentifier & { cloudId: string },
	signal?: AbortSignal,
): Promise<ApiReadViewWorkflowResponse> => {
	const { cloudId, ...workflowIdentifier } = requestData;
	const url = `/${getApiPrefix()}/query/${cloudId}/2/workflow/read-view`;
	const payload = getPayload(workflowIdentifier);
	const result = await performPostRequest(url, {
		body: JSON.stringify(payload),
		signal,
	});

	try {
		validateLogicalDocumentIntegrity(result);
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		// an error here will not block the user experience, but it triggers
		// a priority alarm for us to investigate and understand the occurrence.
		log.safeErrorWithoutCustomerData('workflow.render.integrity.checker', message);
	}
	return result;
};

export const getReadViewWorkflowNew = async (
	requestData: RequestData,
): Promise<MetaWorkflowDocument> => {
	const result = await fetchReadViewWorkflow(requestData);
	return transform(result);
};

export const getMultiReadViewWorkflow = async (
	requestData: RequestData,
	signal?: AbortSignal,
): Promise<MetaWorkflowDocument[]> => {
	const result = await fetchReadViewWorkflow(requestData, signal);
	return transformMultipleWorkflows(result);
};

export default getReadViewWorkflowNew;
